import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  collectResourceFields,
  IResourceFieldAllowance,
} from './resourceFieldAllowances'

/** A workflow type and the fields of one resource type that belong to it. */
export interface IWorkflowTypeFieldGroup {
  typeId: string
  typeName: string
  /** Falls back to UNASSIGNED so the category grouping always has a band to use. */
  categoryName: string
  isInactive: boolean
  iconName: string
  fields: IResourceFieldAllowance[]
}

/**
 * Every workflow type paired with its fields of `resourceTypeId`, each carrying whether this
 * record is allowed by that field's own allow-list.
 *
 * Reads only - no query of its own. The whole field tree, allow-lists included, already
 * arrives with the workflow types the app loads at startup, so this is a projection over
 * data the client holds.
 *
 * Types with no field of this resource type are returned with an empty `fields` list rather
 * than dropped, so the caller decides whether to show or hide them.
 */
export function useResourceFieldAllowances(
  resourceTypeId: string,
  recordId: string,
): IWorkflowTypeFieldGroup[] {
  const { workflowTypesStore, workflowCategoriesStore } = React.useContext(
    MobXProviderContext,
  ) as {
    workflowTypesStore: WorkflowTypesStore
    workflowCategoriesStore: WorkflowCategoriesStore
  }

  // Not memoized on the stores' contents: every read here is observable, and an observer
  // caller re-runs this when any of them changes. A memo keyed on them would have to
  // enumerate the same reads to stay correct.
  return (workflowTypesStore?.actualTypes ?? []).map(type => ({
    typeId: type.id,
    typeName: type.name,
    categoryName: type.getCategoryName(workflowCategoriesStore) || UNASSIGNED,
    isInactive: type.isInactive,
    iconName: type.iconName ?? '',
    fields: collectResourceFields(type, resourceTypeId, recordId),
  }))
}
